import {
    AdminOrder,
    AdminOrderItem,
    OrderListParams,
    OrderStatus,
} from "../../components/models/order/order";
import axiosInstance, { apiError } from "../axiosIntance";

export interface CreateOrderPayload {
    customerInfo: {
        customer_email: string | null;
        first_name: string;
        last_name: string;
        phone_number: string;
    };
    addressInfo: {
        city: string;
        state: string;
        address_description: string;
    };
    paymentInfo: {
        payment_provider: 'delivery' | 'bank' | 'wishmoney';
        payment_id?: string;
        status?: string;
    };
    items: { product_id: number; quantity: number }[];
}

export interface CreateOrderResponse {
    message: string;
    orderId: number;
    totalQuantity: number;
    grandTotal: number;
}

/**
 * A deleted product leaves its order line behind with a null product_id and the
 * name snapshot intact, so the id stays nullable rather than falling back to 0.
 */
const normalizeItem = (raw: any): AdminOrderItem => ({
    id: raw?.id,
    orderId: raw?.order_id,
    productId: raw?.product_id ?? null,
    productName: raw?.product_name ?? 'Deleted product',
    quantity: raw?.quantity ?? 0,
    unitPrice: raw?.unit_price ?? 0,
    lineTotal: raw?.line_total ?? 0,
});

/**
 * The admin list is a SQL join, so it arrives flat and snake_cased apart from
 * the nested `items`. Money fields are already numeric.
 */
const normalizeOrder = (raw: any): AdminOrder => ({
    // The list endpoint names the key order_id; /orders/my calls the same thing id.
    id: raw?.order_id ?? raw?.id,
    customerEmail: raw?.customer_email ?? '',
    firstName: raw?.first_name ?? '',
    lastName: raw?.last_name ?? '',
    phoneNumber: raw?.phone_number ?? '',
    status: raw?.status ?? 'pending',
    createdAt: raw?.created_at ?? '',
    subtotal: raw?.subtotal ?? 0,
    grandTotal: raw?.grand_total ?? 0,

    city: raw?.city ?? '',
    state: raw?.state ?? '',
    addressDescription: raw?.address_description ?? '',

    paymentProvider: raw?.payment_provider ?? '',
    paymentId: raw?.payment_id ?? '',
    paymentStatus: raw?.payment_status ?? '',
    paymentAmount: raw?.payment_amount ?? 0,

    items: Array.isArray(raw?.items) ? raw.items.map(normalizeItem) : [],
    totalQuantity: raw?.total_quantity ?? 0,
});

export const OrderServices = {
    /**
     * GET /api/orders — admin only, newest first.
     * Every filter is optional; omitting `limit` returns the whole list. The API
     * rejects `offset` on its own with a 400, so it is only sent alongside a limit.
     */
    getOrders: async (params: OrderListParams = {}): Promise<AdminOrder[]> => {
        const query: Record<string, string | number> = {};
        if (params.status) query.status = params.status;
        if (params.limit !== undefined) {
            query.limit = params.limit;
            if (params.offset !== undefined) query.offset = params.offset;
        }

        try {
            const res = await axiosInstance.get('/orders', { params: query });
            return Array.isArray(res.data) ? res.data.map(normalizeOrder) : [];
        } catch (err: any) {
            throw apiError(err, 'Failed to load orders');
        }
    },

    /**
     * PATCH /api/orders/:id/status — admin only. Answers with the updated order
     * row, which has no line items on it, so callers should keep the items they
     * already hold rather than replacing the whole order.
     */
    updateOrderStatus: async (id: number, status: OrderStatus): Promise<OrderStatus> => {
        try {
            const res = await axiosInstance.patch(`/orders/${id}/status`, { status });
            return res.data?.order?.status ?? status;
        } catch (err: any) {
            throw apiError(err, 'Failed to update the order status');
        }
    },

    createOrder: async (orderData: CreateOrderPayload): Promise<CreateOrderResponse> => {
        try {
            const res = await axiosInstance.post('/orders', orderData);
            return res.data;
        } catch (err: any) {
            // Surface the API message ("Not enough stock for ...") to the checkout form.
            throw apiError(err, 'We could not place your order. Please try again.');
        }
    },
};
